import React from 'react';
import styled from '@emotion/styled';

const Container = styled.main`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SubTitle = styled.h2`
  font-size: 1.5rem;
  color: var(--secondary);
  margin-bottom: 1rem;
`;

const Text = styled.p`
  line-height: 1.8;
  margin-bottom: 1rem;
  color: #2d3748;
`;

const List = styled.ul`
  margin-left: 2rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const StressReliefTips: React.FC = () => {
  return (
    <Container>
      <Title>Understanding and Managing Stress</Title>
      
      <Section>
        <SubTitle>What is Stress?</SubTitle>
        <Text>
          Stress is your body's natural response to challenges and demands. While some stress can be beneficial,
          chronic stress can affect both your physical and mental well-being. Understanding stress is the first
          step to managing it effectively.
        </Text>
      </Section>

      <Section>
        <SubTitle>Common Signs of Stress</SubTitle>
        <List>
          <ListItem>Difficulty sleeping or changes in sleep patterns</ListItem>
          <ListItem>Frequent headaches or muscle tension</ListItem>
          <ListItem>Changes in appetite</ListItem>
          <ListItem>Difficulty concentrating</ListItem>
          <ListItem>Feeling overwhelmed or anxious</ListItem>
          <ListItem>Irritability or mood changes</ListItem>
        </List>
      </Section>

      <Section>
        <SubTitle>Quick Stress Relief Techniques</SubTitle>
        <List>
          <ListItem><strong>Deep Breathing:</strong> Try our 4-7-8 breathing exercise</ListItem>
          <ListItem><strong>Progressive Relaxation:</strong> Tense and relax each muscle group</ListItem>
          <ListItem><strong>Mindful Walking:</strong> Take a short walk while focusing on your surroundings</ListItem>
          <ListItem><strong>Visualization:</strong> Imagine a peaceful, calming place</ListItem>
          <ListItem><strong>Quick Exercise:</strong> A 5-minute stretch or brief walk can help</ListItem>
        </List>
      </Section>

      <Section>
        <SubTitle>Long-term Stress Management</SubTitle>
        <List>
          <ListItem><strong>Regular Exercise:</strong> Aim for 30 minutes of moderate activity daily</ListItem>
          <ListItem><strong>Healthy Diet:</strong> Balanced nutrition helps your body cope with stress</ListItem>
          <ListItem><strong>Adequate Sleep:</strong> Maintain a consistent sleep schedule</ListItem>
          <ListItem><strong>Time Management:</strong> Prioritize tasks and break large projects into smaller steps</ListItem>
          <ListItem><strong>Social Connection:</strong> Stay connected with supportive friends and family</ListItem>
          <ListItem><strong>Mindfulness Practice:</strong> Regular meditation or mindfulness exercises</ListItem>
        </List>
      </Section>

      <Section>
        <SubTitle>When to Seek Professional Help</SubTitle>
        <Text>
          If stress is significantly impacting your daily life, don't hesitate to seek professional help.
          Mental health professionals can provide additional tools and strategies tailored to your specific
          situation. Remember, seeking help is a sign of strength, not weakness.
        </Text>
      </Section>
    </Container>
  );
};

export default StressReliefTips; 